import {
  adrEnvelopeSchema,
  createEndoscopyReportRequestSchema,
  createPolypRequestSchema,
  createProcedureConsentRequestSchema,
  endoscopyReportEnvelopeSchema,
  endoscopyReportListEnvelopeSchema,
  polypEnvelopeSchema,
  procedureConsentEnvelopeSchema,
  procedureConsentListEnvelopeSchema,
  recordPolypPathologyRequestSchema,
  type AdrResponse,
  type CreateEndoscopyReportRequest,
  type CreatePolypRequest,
  type CreateProcedureConsentRequest,
  type EndoscopyReportResponse,
  type PolypResponse,
  type ProcedureConsentResponse,
  type RecordPolypPathologyRequest,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * The gastroenterology specialty-module API surface (`BACKLOG.md` M5), validated against
 * `@emr/shared`'s own request/response schemas — the exact same schemas `server/src/gastroenterology/
 * routes.ts` validates request bodies with, matching `api/aesthetics.ts`'s/`api/dermatology.ts`'s own
 * stated reasoning for why this cannot silently drift from what the server actually accepts and
 * returns.
 */

export type {
  AdrResponse,
  CreateEndoscopyReportRequest,
  CreatePolypRequest,
  CreateProcedureConsentRequest,
  EndoscopyReportResponse,
  PolypResponse,
  ProcedureConsentResponse,
  RecordPolypPathologyRequest,
};

function gi(patientId: string, suffix = ''): string {
  return `/api/patients/${patientId}/gastroenterology${suffix}`;
}

// --- Procedure consent ------------------------------------------------------------------------------

export async function fetchProcedureConsents(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly ProcedureConsentResponse[]> {
  const response = await apiRequest(
    gi(patientId, '/consents'),
    procedureConsentListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.consents;
}

export async function createProcedureConsent(
  patientId: string,
  input: CreateProcedureConsentRequest,
): Promise<ProcedureConsentResponse> {
  createProcedureConsentRequestSchema.parse(input);
  const response = await apiRequest(gi(patientId, '/consents'), procedureConsentEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.consent;
}

// --- Endoscopy reports -------------------------------------------------------------------------------

export async function fetchEndoscopyReports(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly EndoscopyReportResponse[]> {
  const response = await apiRequest(
    gi(patientId, '/reports'),
    endoscopyReportListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.reports;
}

export async function createEndoscopyReport(
  patientId: string,
  input: CreateEndoscopyReportRequest,
): Promise<EndoscopyReportResponse> {
  createEndoscopyReportRequestSchema.parse(input);
  const response = await apiRequest(gi(patientId, '/reports'), endoscopyReportEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.report;
}

// --- Polyps -------------------------------------------------------------------------------------

export async function createPolyp(
  patientId: string,
  reportId: string,
  input: CreatePolypRequest,
): Promise<PolypResponse> {
  createPolypRequestSchema.parse(input);
  const response = await apiRequest(
    gi(patientId, `/reports/${reportId}/polyps`),
    polypEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.polyp;
}

export async function recordPolypPathology(
  patientId: string,
  reportId: string,
  polypId: string,
  input: RecordPolypPathologyRequest,
): Promise<PolypResponse> {
  recordPolypPathologyRequestSchema.parse(input);
  const response = await apiRequest(
    gi(patientId, `/reports/${reportId}/polyps/${polypId}/pathology`),
    polypEnvelopeSchema,
    { method: 'PATCH', body: input },
  );
  return response.polyp;
}

// --- Adenoma Detection Rate (org-wide, provider-scoped) -------------------------------------------

export async function fetchAdenomaDetectionRate(
  providerId: string,
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<AdrResponse> {
  const query = new URLSearchParams({ providerId, from, to });
  const response = await apiRequest(
    `/api/gastroenterology/adr?${query.toString()}`,
    adrEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.adr;
}
